/**
 * Requête WCS DescribeCoverage — estimation grossière de l'espacement de grille.
 *
 * GeoMet expose, en plus du WMS, un service WCS où DescribeCoverage retourne
 * un <gml:offsetVector> par axe de grille. Pour les modèles à grille tournée
 * (HRDPS notamment), ce pas est exprimé dans le repère natif du modèle mais
 * étiqueté "EPSG:4326" par simplification du serveur — ce n'est donc PAS un
 * espacement géographique réel valide partout sur le domaine (un pas constant
 * en vraie lat/lon sur un domaine de plusieurs dizaines de degrés serait
 * géométriquement impossible pour une grille tournée).
 *
 * Le résultat ne doit donc servir que de point de départ pour une recherche
 * du point de grille voisin via GetFeatureInfo (voir series.js du projet
 * wms_time_series), qui donne elle la vraie distance géographique quel que
 * soit le repère natif du modèle.
 */

const WCS_BASE_URL = 'https://geo.weather.gc.ca/geomet?SERVICE=WCS&VERSION=2.0.1';

const cache = new Map();

/**
 * @param {string} coverageId - même nom que la couche WMS, ex: 'HRDPS.CONTINENTAL_TT'
 * @param {AbortSignal} [signal]
 * @returns {Promise<{lonStep: number, latStep: number}>} pas de grille en degrés,
 *   repère natif du modèle (voir avertissement ci-dessus)
 */
export async function fetchGridSpacing(coverageId, signal) {
  if (cache.has(coverageId)) return cache.get(coverageId);

  const url = `${WCS_BASE_URL}&REQUEST=DescribeCoverage&COVERAGEID=${coverageId}`;
  const resp = await fetch(url, signal ? { signal } : {});
  if (!resp.ok) throw new Error(`DescribeCoverage HTTP ${resp.status} pour ${coverageId}`);
  const text = await resp.text();
  const spacing = parseGridSpacing(text);
  cache.set(coverageId, spacing);
  return spacing;
}

/**
 * Extrait le pas de grille depuis le XML DescribeCoverage (WCS 2.0).
 *
 * Chaque <gml:offsetVector> a deux composantes (lat, long) — d'après
 * l'ordre d'axes de l'Envelope ("lat long"), toujours vérifié sur RDPS,
 * GDPS, HRDPS et RAQDPS — dont une seule est non nulle (grille alignée
 * dans son propre repère). On additionne les valeurs absolues de chaque
 * composante sur tous les vecteurs : une seule contribue par composante,
 * ce qui donne directement les deux pas sans dépendre de l'ordre des
 * <gml:offsetVector> dans le document.
 *
 * @param {string} xmlText
 * @returns {{lonStep: number, latStep: number}}
 * @throws {Error} si les <gml:offsetVector> sont introuvables
 */
export function parseGridSpacing(xmlText) {
  const vectors = [...xmlText.matchAll(/<gml:offsetVector[^>]*>([^<]+)<\/gml:offsetVector>/g)]
    .map(m => m[1].trim().split(/\s+/).map(Number));
  if (vectors.length < 2) {
    throw new Error('offsetVector introuvable dans le DescribeCoverage WCS');
  }
  let latStep = 0;
  let lonStep = 0;
  for (const [lat, lon] of vectors) {
    latStep += Math.abs(lat);
    lonStep += Math.abs(lon);
  }
  return { lonStep, latStep };
}
